/**
 * Pengaturan Tampilan Default Tabel Aset (user request 2026-10-22).
 *
 * Admin memilih kolom mana yang menjadi DEFAULT untuk seluruh user.
 * User tetap bisa meng-override lewat dropdown "Kolom" di halaman
 * Inventaris & Aset (tersimpan per user via /user-prefs/table/assets).
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Columns3, Save, RotateCcw, Info } from "lucide-react";
import api, { formatDetail } from "@/lib/api";
import { ASSET_COLUMN_DEFS, ASSET_DEFAULT_COLUMNS } from "@/lib/assetColumns";

export default function AssetColumnSettings() {
  const [cols, setCols] = useState(ASSET_DEFAULT_COLUMNS);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/settings/asset-columns");
      if (Array.isArray(data?.columns) && data.columns.length) setCols(data.columns);
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail));
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = (key) => setCols((cur) =>
    cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key]);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/settings/asset-columns", { columns: cols });
      toast.success("Tampilan default tersimpan — berlaku untuk user tanpa pengaturan personal.");
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const reset = async () => {
    if (!window.confirm("Kembalikan tampilan default ke bawaan sistem?")) return;
    setBusy(true);
    try {
      await api.delete("/settings/asset-columns");
      setCols(ASSET_DEFAULT_COLUMNS);
      toast.success("Tampilan default dikembalikan ke bawaan.");
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-slate-200 max-w-2xl" data-testid="asset-column-settings">
      <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2">
        <Columns3 size={15} className="text-[#0B1D3A]" />
        <div>
          <h3 className="font-display text-sm uppercase tracking-wider text-[#0B1D3A]">Tampilan Default Tabel Aset</h3>
          <p className="text-[11px] text-slate-500">Kolom yang tampil secara default di Inventaris &amp; Aset untuk semua user.</p>
        </div>
      </div>

      <div className="p-4 grid grid-cols-2 md:grid-cols-3 gap-1.5">
        {loading ? (
          <div className="col-span-3 text-center text-slate-400 text-sm italic py-4">Memuat…</div>
        ) : ASSET_COLUMN_DEFS.map((d) => (
          <label key={d.key} data-testid={`asset-col-def-${d.key}`}
            className={`flex items-center gap-2 border px-2 py-1.5 text-[11px] cursor-pointer ${cols.includes(d.key) ? "border-[#FF5A1F] bg-orange-50" : "border-slate-200 hover:border-slate-400"}`}>
            <input type="checkbox" checked={cols.includes(d.key)} onChange={() => toggle(d.key)} />
            <span>{d.label}</span>
          </label>
        ))}
      </div>

      <div className="px-4 py-3 border-t border-slate-200 bg-slate-50 flex items-center gap-2">
        <button type="button" onClick={reset} disabled={busy} data-testid="asset-col-default-reset"
          className="h-8 px-3 text-[10px] uppercase tracking-wider font-semibold border border-slate-300 text-slate-700 hover:bg-white flex items-center gap-1">
          <RotateCcw size={11} /> Bawaan
        </button>
        <button type="button" onClick={save} disabled={busy || !cols.length} data-testid="asset-col-default-save"
          className="ml-auto h-8 px-3 text-[10px] uppercase tracking-wider font-bold border border-[#0B1D3A] bg-[#0B1D3A] text-white hover:bg-[#153162] flex items-center gap-1 disabled:opacity-50">
          <Save size={11} /> {busy ? "Menyimpan…" : "Simpan Default"}
        </button>
      </div>

      <div className="px-4 py-3 border-t border-slate-200 text-[11px] text-slate-500 flex items-start gap-1.5">
        <Info size={11} className="mt-0.5 shrink-0" />
        Kolom No. Asset, Nama, dan Aksi selalu tampil. User dapat menyesuaikan kolomnya
        sendiri lewat tombol "Kolom" di halaman Inventaris &amp; Aset — pengaturan
        personal mengalahkan default ini.
      </div>
    </div>
  );
}
